import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { ENV } from "~/config/env.config";
import {
  retryImpersonationHandoff,
  useImpersonationHandoffStore,
} from "~/lib/auth/impersonation-handoff";
import { Card } from "~/components/ui/card";

export const HANDOFF_INVALID_TITLE =
  "This support link has expired or was already used";
export const HANDOFF_UNAVAILABLE_TITLE = "We couldn't start the support session";

/**
 * OPS-3.5 — what an Ops Panel support link shows when it does not sign the
 * tab in. Rendered by root.tsx's App in place of the routes, so a refused
 * link never falls through to a route guard that would bounce the agent to a
 * restaurant login screen they have no credentials for.
 *
 * Returns null while there is nothing to report.
 */
export function ImpersonationHandoffNotice() {
  const failure = useImpersonationHandoffStore((s) => s.failure);
  const isRetrying = useImpersonationHandoffStore((s) => s.isRetrying);
  if (!failure) return null;

  const isInvalid = failure.kind === "invalid";
  const opsPanelUrl = ENV.OPS_PANEL_URL;

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card role="alert" padding="default" className="w-full max-w-lg p-10 text-center">
        <div className="mx-auto mb-6 flex size-14 items-center justify-center rounded-full bg-red-50">
          <AlertCircle className="size-7 text-red-700" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-semibold text-main">
          {isInvalid ? HANDOFF_INVALID_TITLE : HANDOFF_UNAVAILABLE_TITLE}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {isInvalid
            ? "Support links work once and only for a couple of minutes. Start a new support session for this restaurant from the Ops Panel."
            : "The link may still be valid for a moment. Check your connection and try again, or start a new session from the Ops Panel."}
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          {!isInvalid && (
            <Button
              type="button"
              onClick={() => void retryImpersonationHandoff()}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />
                  Trying again…
                </>
              ) : (
                "Try again"
              )}
            </Button>
          )}
          {opsPanelUrl && (
            <Button asChild variant={isInvalid ? "default" : "outline"}>
              <a href={opsPanelUrl}>Back to the Ops Panel</a>
            </Button>
          )}
        </div>
      </Card>
    </main>
  );
}
